const ASSET_COLLECTION = "assets";
const ADCREATIVE_COLLECTION = "adcreatives";

function AssetsMixin(firebase) {
  return {
    async createAsset({ asset }) {
      const ref = await this.collection(ASSET_COLLECTION).add(asset);
      return {
        id: ref.id,
        ...asset
      };
    },

    async updateAssets({ assets }) {
      const batch = firebase.firestore().batch();
      assets.forEach((asset) => {
        const { id } = asset;
        delete asset.id;
        const assetRef = this.collection(ASSET_COLLECTION).doc(id);
        batch.set(assetRef, asset, { merge: true });
      });

      return batch.commit();
    },

    async deleteAssetAttributes({ assetIds, attributesToDelete }) {
      const batch = firebase.firestore().batch();

      assetIds.forEach((id) => {
        const assetRef = this.collection(ASSET_COLLECTION).doc(id);
        const updates = {};
        attributesToDelete.forEach((attribute) => {
          updates[attribute] = firebase.firestore.FieldValue.delete();
        });

        batch.update(assetRef, updates);
      });

      return batch.commit();
    },

    async updateAdCreativesBatch(adCreatives = []) {
      const batch = firebase.firestore().batch();
      adCreatives.forEach((adCreative) => {
        const { id } = adCreative;
        delete adCreative.id;
        const assetRef = this.collection(ADCREATIVE_COLLECTION).doc(id);
        batch.set(assetRef, adCreative, { merge: true });
      });

      return batch.commit();
    },

    async deleteAsset({ id }) {
      return await this.collection(ASSET_COLLECTION).doc(id).delete();
    },

    async findAdcreativesByCampaignId({ campaignId, filters = {} }) {
      const allowedFields = ["type", "channel"];
      let query = this.collection(ADCREATIVE_COLLECTION).where("campaignId", "==", campaignId);

      // Apply each filter to the query.
      for (let [field, value] of Object.entries(filters)) {
        if (!allowedFields.includes(field)) {
          throw new Error(`Invalid filter field: ${field}`);
        }

        if (Array.isArray(value)) {
          // Firestore has a limit for using `whereIn`, it supports up to 10 values in an array
          if (value.length > 10) {
            throw new Error(`${field} must have fewer than 10 values`);
          }
          query = query.where(field, "in", value);
        } else {
          query = query.where(field, "==", value);
        }
      }

      const snapshots = await query.get();

      return snapshots.docs.map(processDoc);
    },

    async findAdcreativesByOrgId({ orgId }) {
      const snapshots = await this.collection(ADCREATIVE_COLLECTION)
        .where("orgaId", "==", orgId)
        .get();

      return snapshots.docs.map(processDoc);
    },

    async findAdcreativesByCampaignIdAndType({ campaignId, type }) {
      const snapshots = await this.collection(ADCREATIVE_COLLECTION)
        .where("campaignId", "==", campaignId)
        .where("type", "==", type)
        .get();

      return snapshots.docs.map(processDoc);
    },

    async findOrgaAssetsByExternalId({ orgaId, externalId }) {
      const snapshots = await this.collection(ASSET_COLLECTION)
        .where("orgaId", "==", orgaId)
        .where("externalId", "==", externalId)
        .get();

      return snapshots.docs.map(processDoc);
    },

    async findOrgaCompanyAssetsByExternalId({ orgaId, companyId, externalId }) {
      const snapshots = await this.collection(ASSET_COLLECTION)
        .where("orgaId", "==", orgaId)
        .where("companyId", "==", companyId)
        .where("externalId", "==", externalId)
        .get();

      return snapshots.docs.map(processDoc);
    },

    async findOrgaAssets({ orgaId }) {
      const snapshots = await this.collection(ASSET_COLLECTION).where("orgaId", "==", orgaId).get();
      return snapshots.docs.map(processDoc);
    },

    async findAssetByUrl({ url }) {
      const snapshots = await this.collection(ASSET_COLLECTION).where("url", "==", url).get();
      return snapshots.docs.map(processDoc);
    },

    async findAssetsByUnlicensedUrlAndCompanyId({ url, companyId }) {
      const snapshots = await this.collection(ASSET_COLLECTION)
        .where("shutterstock.unlicensedUrl", "==", url)
        .where("companyId", "==", companyId)
        .get();
      return snapshots.docs.map(processDoc);
    },

    async findAssetsByIds({ ids }) {
      if (!Array.isArray(ids) || ids.length === 0) {
        return [];
      }

      const snapshot = await this.collection(ASSET_COLLECTION)
        .where(firebase.firestore.FieldPath.documentId(), "in", ids)
        .get();

      return snapshot.docs.map(processDoc);
    },

    async getAllAssets() {
      const snapshot = await this.collection(ASSET_COLLECTION).get();

      return snapshot.docs.map(processDoc);
    },

    /**
     * Asynchronously retrieves an asset by its unique identifier from the specified collection.
     *
     * @async
     * @function
     * @param {Object} params - Parameters for finding the asset.
     * @param {string} params.id - The unique identifier of the asset.
     * @returns {Promise<Object>} A promise that resolves to the retrieved asset.
     */
    async findAssetById({ id }) {
      const snapshot = await this.collection(ASSET_COLLECTION).doc(id).get();
      return processDoc(snapshot);
    }
  };
}

const processDoc = (doc) => {
  return { id: doc.id, ...doc.data() };
};

module.exports = AssetsMixin;
